import React, { useState, useEffect, useRef, useCallback } from 'react';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import './on-screen-keyboard.css';

/*
 * OnScreenKeyboard
 *
 * A coffee-shop themed multi-language virtual keyboard.
 *
 * Usage:
 *   - Wrap any view with <OnScreenKeyboardProvider> (App.js does this once globally).
 *   - Mark any input/textarea with attribute `data-osk="true"` to auto-attach.
 *   - On focus, the keyboard slides up from the bottom; clicking outside closes it.
 *   - Language switcher at bottom: English / Arabic / Russian / Other (numeric).
 *
 * Implementation details:
 *   - Uses native input events (input.setRangeText / setSelectionRange) so React-controlled
 *     inputs continue to work without breaking onChange handlers.
 *   - Layout flips RTL automatically for Arabic.
 */

const LAYOUTS = {
  english: {
    default: [
      '` 1 2 3 4 5 6 7 8 9 0 - = {bksp}',
      '{tab} q w e r t y u i o p [ ] \\',
      '{lock} a s d f g h j k l ; \' {enter}',
      '{shift} z x c v b n m , . / {shift}',
      '{space}',
    ],
    shift: [
      '~ ! @ # $ % ^ & * ( ) _ + {bksp}',
      '{tab} Q W E R T Y U I O P { } |',
      '{lock} A S D F G H J K L : " {enter}',
      '{shift} Z X C V B N M < > ? {shift}',
      '{space}',
    ],
  },
  arabic: {
    default: [
      'ذ 1 2 3 4 5 6 7 8 9 0 - = {bksp}',
      '{tab} ض ص ث ق ف غ ع ه خ ح ج د \\',
      '{lock} ش س ي ب ل ا ت ن م ك ط {enter}',
      '{shift} ئ ء ؤ ر لا ى ة و ز ظ {shift}',
      '{space}',
    ],
    shift: [
      'ّ ! @ # $ % ^ & * ) ( _ + {bksp}',
      '{tab} َ ً ُ ٌ لإ إ ` ÷ × ؛ < > |',
      '{lock} ِ ٍ ] [ لأ أ ـ ، / : " {enter}',
      '{shift} ~ ْ } { لآ آ \' , . ؟ {shift}',
      '{space}',
    ],
  },
  russian: {
    default: [
      'ё 1 2 3 4 5 6 7 8 9 0 - = {bksp}',
      '{tab} й ц у к е н г ш щ з х ъ \\',
      '{lock} ф ы в а п р о л д ж э {enter}',
      '{shift} я ч с м и т ь б ю . {shift}',
      '{space}',
    ],
    shift: [
      'Ё ! " № ; % : ? * ( ) _ + {bksp}',
      '{tab} Й Ц У К Е Н Г Ш Щ З Х Ъ /',
      '{lock} Ф Ы В А П Р О Л Д Ж Э {enter}',
      '{shift} Я Ч С М И Т Ь Б Ю , {shift}',
      '{space}',
    ],
  },
  other: {
    // Numeric / symbols pad for phone numbers etc.
    default: [
      '1 2 3 {bksp}',
      '4 5 6 -',
      '7 8 9 +',
      '* 0 # {enter}',
    ],
    shift: [
      '1 2 3 {bksp}',
      '4 5 6 -',
      '7 8 9 +',
      '* 0 # {enter}',
    ],
  },
};

const DISPLAY = {
  '{bksp}': '⌫',
  '{enter}': '⏎',
  '{shift}': '⇧',
  '{tab}': 'Tab',
  '{lock}': 'Caps',
  '{space}': ' ',
};

const LANGS = [
  { code: 'english', label: 'ENGLISH', flag: '🇬🇧' },
  { code: 'arabic',  label: 'العربية', flag: '🇸🇦' },
  { code: 'russian', label: 'Русский', flag: '🇷🇺' },
  { code: 'other',   label: 'Other',   flag: '#' },
];

const STORAGE_LANG_KEY = 'osk_language';

// Global state managed via custom event so we don't need React context everywhere
let activeInput = null;

const dispatchOSKEvent = (type, detail = {}) => {
  window.dispatchEvent(new CustomEvent(`osk:${type}`, { detail }));
};

const insertAtCursor = (input, text) => {
  if (!input) return;
  // Re-grab focus so future keys keep working & the caret stays visible
  try { input.focus({ preventScroll: true }); } catch (_) {}
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? input.value.length;
  const before = input.value.substring(0, start);
  const after = input.value.substring(end);
  const newValue = before + text + after;

  // Use native setter so React picks up the change
  const proto = input.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
  setter.call(input, newValue);

  // Move cursor
  const newPos = start + text.length;
  try { input.setSelectionRange(newPos, newPos); } catch (_) { /* not all inputs support */ }

  // Fire input event so React's onChange runs
  input.dispatchEvent(new Event('input', { bubbles: true }));
};

const backspaceAtCursor = (input) => {
  if (!input) return;
  try { input.focus({ preventScroll: true }); } catch (_) {}
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? input.value.length;
  if (start === 0 && end === 0) return;
  let newStart;
  let newValue;
  if (start === end) {
    newStart = start - 1;
    newValue = input.value.substring(0, newStart) + input.value.substring(end);
  } else {
    newStart = start;
    newValue = input.value.substring(0, start) + input.value.substring(end);
  }
  const proto = input.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
  setter.call(input, newValue);
  try { input.setSelectionRange(newStart, newStart); } catch (_) {}
  input.dispatchEvent(new Event('input', { bubbles: true }));
};

const OnScreenKeyboard = ({ enabled = true }) => {
  const [visible, setVisible] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem(STORAGE_LANG_KEY) || 'english');
  const [layoutName, setLayoutName] = useState('default');
  const keyboardRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_LANG_KEY, lang);
  }, [lang]);

  // Track which input the user last focused, so the keyboard knows where to type.
  // We do NOT auto-open the keyboard on focus anymore — it is opened manually
  // via the floating toggle button (see OSKToggle below) or via the
  // window event `osk:show` / `osk:hide`.
  useEffect(() => {
    if (!enabled) return undefined;

    const isTypable = (el) => {
      if (!el || !el.tagName) return false;
      const tag = el.tagName.toUpperCase();
      if (tag === 'TEXTAREA') return true;
      if (tag === 'INPUT') {
        const type = (el.getAttribute('type') || 'text').toLowerCase();
        // Skip buttons / checkboxes / files / hidden, etc.
        return ['text', 'search', 'email', 'tel', 'url', 'number', 'password'].includes(type);
      }
      return el.isContentEditable === true;
    };

    const handleFocusIn = (e) => {
      const el = e.target;
      if (isTypable(el)) {
        activeInput = el;
      }
    };
    const handleFocusOut = () => {
      // Don't drop the activeInput aggressively — user might tap a key in
      // the keyboard which momentarily moves focus.
      setTimeout(() => {
        const next = document.activeElement;
        if (!next) return;
        if (isTypable(next)) {
          activeInput = next;
          return;
        }
        // If focus moved inside the keyboard panel, keep last input.
        if (next.closest && next.closest('.osk-root')) return;
        // If body, keep last known typable input if still in DOM.
        if (next === document.body && activeInput && document.body.contains(activeInput)) return;
        // Otherwise forget it.
        activeInput = null;
      }, 120);
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    const handleManualShow = () => setVisible(true);
    const handleManualHide = () => { setVisible(false); };
    const handleManualToggle = () => setVisible((v) => !v);
    window.addEventListener('osk:show', handleManualShow);
    window.addEventListener('osk:hide', handleManualHide);
    window.addEventListener('osk:toggle', handleManualToggle);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      window.removeEventListener('osk:show', handleManualShow);
      window.removeEventListener('osk:hide', handleManualHide);
      window.removeEventListener('osk:toggle', handleManualToggle);
    };
  }, [enabled]);

  const handleKeyPress = useCallback((button) => {
    if (!activeInput) return;
    if (button === '{shift}' || button === '{lock}') {
      setLayoutName((prev) => (prev === 'default' ? 'shift' : 'default'));
      return;
    }
    if (button === '{bksp}') {
      backspaceAtCursor(activeInput);
      return;
    }
    if (button === '{tab}') {
      insertAtCursor(activeInput, '\t');
      return;
    }
    if (button === '{enter}') {
      // Insert newline for textarea, otherwise submit form
      if (activeInput.tagName === 'TEXTAREA') {
        insertAtCursor(activeInput, '\n');
      } else {
        // Trigger Enter
        activeInput.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
      }
      return;
    }
    if (button === '{space}') {
      insertAtCursor(activeInput, ' ');
      return;
    }
    // Regular character
    insertAtCursor(activeInput, button);
    if (layoutName === 'shift') setLayoutName('default'); // one-shot shift
  }, [layoutName]);

  if (!enabled) return null;

  // Always render the floating toggle button (and the panel only when visible)
  const isRTL = lang === 'arabic';

  return (
    <>
      {/* Floating keyboard toggle button (always visible when OSK is enabled) */}
      <button
        type="button"
        className={`osk-toggle-fab ${visible ? 'osk-toggle-fab--active' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setVisible((v) => !v)}
        title={visible ? 'Hide on-screen keyboard' : 'Show on-screen keyboard'}
        aria-label={visible ? 'Hide on-screen keyboard' : 'Show on-screen keyboard'}
        data-testid="osk-toggle-fab"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect width="20" height="16" x="2" y="4" rx="2"/>
          <path d="M6 8h.01"/>
          <path d="M10 8h.01"/>
          <path d="M14 8h.01"/>
          <path d="M18 8h.01"/>
          <path d="M6 12h.01"/>
          <path d="M10 12h.01"/>
          <path d="M14 12h.01"/>
          <path d="M18 12h.01"/>
          <path d="M7 16h10"/>
        </svg>
      </button>

      {visible && (
        <div className={`osk-root ${isRTL ? 'osk-rtl' : ''}`} role="dialog" aria-label="On-screen keyboard">
          <div className="osk-toolbar">
            <span className="osk-toolbar__title">⌨ Keyboard</span>
            <div className="osk-toolbar__spacer" />
            <button
              type="button"
              className="osk-toolbar__close"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setVisible(false); }}
              aria-label="Hide keyboard"
            >
              ✕
            </button>
          </div>

          <div className="osk-keyboard-wrap" onMouseDown={(e) => e.preventDefault()}>
            <Keyboard
              keyboardRef={(r) => { keyboardRef.current = r; }}
              layoutName={layoutName}
              layout={LAYOUTS[lang]}
              display={DISPLAY}
              onKeyPress={handleKeyPress}
              theme={`hg-theme-default osk-theme-coffee ${isRTL ? 'osk-keyboard-rtl' : ''}`}
              physicalKeyboardHighlight={false}
              preventMouseDownDefault
            />
          </div>

          <div className="osk-lang-row" onMouseDown={(e) => e.preventDefault()}>
            <span className="osk-lang-row__label">LANGUAGE</span>
            {LANGS.map(({ code, label, flag }) => (
              <button
                key={code}
                type="button"
                className={`osk-lang-btn ${lang === code ? 'osk-lang-btn--active' : ''}`}
                onClick={() => setLang(code)}
              >
                <span className="osk-lang-btn__flag" aria-hidden>{flag}</span>
                <span className="osk-lang-btn__label">[{label}]</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// Helpers for programmatic show/hide and toggling
export const showOSK = () => dispatchOSKEvent('show');
export const hideOSK = () => dispatchOSKEvent('hide');
export const toggleOSK = () => dispatchOSKEvent('toggle');

export default OnScreenKeyboard;
